import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UserReviewClient, ReviewPlan, AdminAuditEntry } from 'src/app/shared/services/ppv.api'
import { DVM_REVIEW_TEMPLATE } from '../dvm-tool-plan/review-template-dvm'
import { HM_REVIEW_TEMPLATE } from '../dvm-tool-plan/review-template-hm'
import { RM_REVIEW_TEMPLATE } from '../dvm-tool-plan/review-template-rm'
import { COS_REVIEW_TEMPLATE } from '../dvm-tool-plan/review-template-cos'
import { DO_REVIEW_TEMPLATE } from '../dvm-tool-plan/review-template-do'
import { ReviewTemplate } from '../dvm-tool-plan/components/review-assessment-sections/review-assessment-sections.component'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { GlobalAdminService } from 'src/app/dvm-tool/globalAdmins.service'
import { ToastrService } from 'ngx-toastr'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export interface CycleConfigEntry {
   key: string
   isEnabled: boolean
   openDate: string
   closeDate: string
}

interface QuestionCard {
   leftTitle: string
   subtitle: string
   agreementScore: number
   revieweeDistribution: { label: string; percent: number }[]
   reviewerDistribution: { label: string; percent: number }[]
}

interface RecentUpdate {
   revieweeName: string
   hospital: string
   status: string
   updatedOn: Date
}

interface ViewModel {
   totalReviews: number
   dvmSubmitted: number
   cosSubmitted: number
   avgAgreement: number
   questionCards: QuestionCard[]
   lendAHandYes: number
   leaderYes: number
   topMedicalInterests: { label: string; count: number }[]
   recentlyUpdated: RecentUpdate[]
   revCompleted: number
}

@Component({
   selector: 'app-reporting-dashboard',
   templateUrl: './reporting-dashboard.component.html',
   styleUrls: ['./reporting-dashboard.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class ReportingDashboardComponent implements OnInit {
   activeTab: 'home' | 'ai' | 'analytics' | 'cycles' | 'users' | 'exports' | 'audit' = 'home'

   pendingUserSelect: any = null
   pendingDeferralId: number | null = null
   panelOpened = false
   settingsOpen = false
   settingsMenuOpen = false
   settingsTab: 'roles' | 'reminders' | 'messages' = 'reminders'
   settingsMessageCount = 0

   isReportingAdmin = this.globalAdminService.isReportingAdmin

   //  Analytics
   reviewType: string = 'DVM'
   loading = true
   error: string | null = null
   vm: ViewModel | null = null

   filterSearch: string = ''
   filterHospital: string | null = null
   filterReviewer: string | null = null
   filterJobCodes: string | null = null
   filterCycle: string | null = null

   selectedDvmCycle: 'Q1' | 'Q2' | 'Q3' = 'Q3'
   filtersCollapsed = false
   allAnalyticsLoaded = false

   private allReviews: any[] = []
   private allResponses: any[] = []

   private templates: { [key: string]: ReviewTemplate } = {
      DVM: DVM_REVIEW_TEMPLATE,
      HM: HM_REVIEW_TEMPLATE,
      RM: RM_REVIEW_TEMPLATE,
      COS: COS_REVIEW_TEMPLATE,
      DO: DO_REVIEW_TEMPLATE,
   }

   allUsers: ReviewPlan[] = []
   filteredUsers: ReviewPlan[] = []
   usersLoading = false
   hospitalOptions: string[] = []
   jobCodeOptions: string[] = []

   exportReviewType = ''
   exportRoleFilter = ''
   exportLoading = false
   clickedExportType = ''
   exportRevieweeOptions: { id: number; name: string }[] = []
   exportRevieweeOptionsLoading = false
   exportSelectedRevieweeId: number | string | null = null

   auditLog: AdminAuditEntry[] = []
   auditLoading = false
   sortAuditColumn: 'timestamp' | 'adminName' | 'action' | 'rcReasonInput' | 'targetUserName'
   sortDirection: 'asc' | 'desc' = 'asc'

   allAuditLog: any[] = []
   allFilters: string[] = [
      '', // 0 = All Activity
      'Deferred - Open Cycle', // 1 = Deferrals
      'Cycle Changed', // 2 = Cycle Changes
      'Reviewer Changed', // 3
      'Primary Reviewer Changed', // 4
      'Hospital Reviewer Changed', // 5
   ]
   auditTabs = 0

   showBackToTop = false
   loggedInUser = ''
   loggedInUserId: number

   constructor(
      private route: ActivatedRoute,
      private userReviewClient: UserReviewClient,
      private authMgr: AuthManager,
      private router: Router,
      private toastr: ToastrService,
      public globalAdminService: GlobalAdminService,
      private cdr: ChangeDetectorRef
   ) {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.loggedInUser = user.displayName
         this.loggedInUserId = user.id
      })
   }

   ngOnInit(): void {
      const navState = this.router.getCurrentNavigation()?.extras?.state ?? history.state
      if (navState?.pendingUserSelect) {
         this.activeTab = 'users'
         this.pendingUserSelect = navState.pendingUserSelect
         this.loadAllUsers()
      }

      // Landing-page search opens in a new tab, so the id arrives as a query param
      const qp = this.route.snapshot.queryParamMap
      if (qp.get('userId') || qp.get('practice') || qp.get('revieweeDisplayName')) {
         this.activeTab = 'users'
         this.pendingUserSelect = {
            revieweeUserId: qp.get('userId'),
            practiceName: qp.get('practice'),
            revieweeDisplayName: qp.get('revieweeDisplayName'),
         }
      }

      this.route.params.subscribe((params) => {
         const tabMap: Record<string, typeof this.activeTab> = {
            home: 'home',
            'ai-insights': 'ai',
            analytics: 'analytics',
            'cycle-management': 'cycles',
            'user-management': 'users',
            'export-data': 'exports',
            'audit-log': 'audit',
         }
         this.activeTab = tabMap[params['section']] ?? 'home'
         this.loadTabData(this.activeTab)
      })
      window.addEventListener('scroll', this.onScroll.bind(this))
      this.loadSettingsMessageCount()
   }

   toggleSettingsMenu(event?: Event): void {
      event?.stopPropagation()
      this.settingsMenuOpen = !this.settingsMenuOpen

      if (this.settingsMenuOpen) {
         this.loadSettingsMessageCount()
      }
   }

   closeSettingsMenu(): void {
      this.settingsMenuOpen = false
   }

   openSettings(tab: 'roles' | 'reminders' | 'messages' = 'reminders'): void {
      this.settingsTab = tab
      this.settingsMenuOpen = false
      this.settingsOpen = true
   }

   closeSettings(): void {
      this.settingsOpen = false
   }

   onSettingsAttentionCountChange(count: number): void {
      this.settingsMessageCount = count
   }

   openSettingsMessageContext(thread: any): void {
      const context = `${thread?.contextType ?? ''} ${thread?.contextLabel ?? ''}`.toLowerCase()
      const deferralId = Number(thread?.contextId)

      if (!context.includes('deferral') || !deferralId) {
         if (thread?.contextUrl) window.location.assign(thread.contextUrl)
         return
      }

      this.settingsOpen = false
      this.settingsMenuOpen = false
      this.activeTab = 'users'
      this.pendingUserSelect = null

      // Pulse the input so clicking the same deferral again will reopen its chat.
      this.pendingDeferralId = null
      this.router.navigate(['/performance-hub/admin', 'user-management'])
      setTimeout(() => (this.pendingDeferralId = deferralId))
   }

   private loadSettingsMessageCount(): void {
      this.userReviewClient.getAdminMessageAttentionCount().subscribe({
         next: (count) => {
            this.settingsMessageCount = count ?? 0
            this.cdr.detectChanges()
         },
         error: () => {
            this.settingsMessageCount = 0
            this.cdr.detectChanges()
         },
      })
   }

   //  Tab navigation

   setTab(tab: 'home' | 'ai' | 'analytics' | 'cycles' | 'users' | 'exports' | 'audit'): void {
      const pathMap: Record<string, string> = {
         home: 'home',
         ai: 'ai-insights',
         analytics: 'analytics',
         cycles: 'cycle-management',
         users: 'user-management',
         exports: 'export-data',
         audit: 'audit-log',
      }
      this.router.navigate(['/performance-hub/admin', pathMap[tab]])
   }

   goToUserManagementDeferrals(): void {
      this.activeTab = 'users'
      this.router.navigate(['/performance-hub/admin', 'user-management'])
   }

   private loadTabData(tab: typeof this.activeTab): void {
      if (tab === 'analytics' && !this.vm) this.loadAnalytics()
      /*if (tab === 'cycles') this.loadCycleConfig()*/
      if (tab === 'users') this.loadAllUsers()
      if (tab === 'audit' && this.auditLog.length === 0) this.loadAuditLog()
   }

   /*loadAllUsers(): void {
      this.usersLoading = true
      this.userReviewClient.getAllAppUsersWithRoles().subscribe({
         next: (users) => {
            if (!users) {
               this.usersLoading = false
               return
            }
            this.allUsers = users.map((u) => ({
               revieweeUserId: u.revieweeUserId,
               revieweeDisplayName: u.revieweeDisplayName,
               practiceName: u.practiceName,
               practiceSKey: u.practiceSKey,
               cosUserId: u.cosUserId,
               vetLeaderUserId: u.vetLeaderUserId,
               rmUserId: u.rmUserId,
               hmUserId: u.hmUserId,
               directorUserId: u.directorUserId,
               vpUserId: u.vpUserId,
               cosDisplayName: u.cosDisplayName,
               vetLeaderDisplayName: u.vetLeaderDisplayName,
               rmDisplayName: u.rmDisplayName,
               hmDisplayName: u.hmDisplayName,
               directorDisplayName: u.directorDisplayName,
               vpDisplayName: u.vpDisplayName,
               jobTitleCode: u.jobTitleCode,
               isDeferred: u.isDeferred,
               deferReason: u.deferReason,
               deferralStatus: u.deferralStatus,
               cycle: u.cycle ? u.cycle : '',
               hireDate: new Date(u.hireDate),
               whenOpenCompletedBy: u.whenOpenCompletedBy,
               cosOverrideName: u.cosOverrideName,
               hmOverrideName: u.hmOverrideName,
               cosaOverrideName: u.cosaOverrideName,
               rmOverrideName: u.rmOverrideName,
               reviewType: u.reviewType,
            }))
            this.hospitalOptions = [...new Set(this.allUsers.map((u) => u.practiceName).filter(Boolean))].sort()
            this.jobCodeOptions = [...new Set(this.allUsers.map((u) => u.jobTitleCode).filter(Boolean))].sort()
            this.filteredUsers = [...this.allUsers]
            this.usersLoading = false

            // pass pendingUserSelect so the child can open the panel
            if (this.pendingUserSelect) {
               const pending = this.pendingUserSelect
               this.pendingUserSelect = null
               setTimeout(() => (this.pendingUserSelect = pending))
            }
         },
         error: () => {
            this.usersLoading = false
         },
      })
   }*/

   private readonly USER_BATCHES = ['DO', 'RM', 'COS', 'HM', 'DVM', 'CLINICAL']
   private batchesRemaining = 0

   loadAllUsers(): void {
      this.allUsers = []
      this.filteredUsers = []
      this.usersLoading = true
      this.batchesRemaining = this.USER_BATCHES.length

      // Complete immediately, so the dropdowns are never missing values mid-load
      this.userReviewClient.getFilterOptions().subscribe({
         next: (o: any) => {
            this.hospitalOptions = o?.hospitals ?? []
            this.jobCodeOptions = o?.jobCodes ?? []
         },
         error: () => {
            /* non-fatal - fall back to whatever the batches provide */
         },
      })

      for (const rt of this.USER_BATCHES) {
         this.userReviewClient.getUsersByTypeWithRoles(rt).subscribe({
            next: (users) => this.appendUserBatch(users ?? []),
            error: (err) => {
               console.error(`User batch ${rt} failed:`, err)
               this.appendUserBatch([])
            },
         })
      }
   }

   private appendUserBatch(users: any[]): void {
      const seen = new Set(this.allUsers.map((u) => u.revieweeUserId))
      const fresh = users.filter((u) => !seen.has(u.revieweeUserId)).map((u) => this.mapUser(u))

      this.allUsers = [...this.allUsers, ...fresh]
      this.filteredUsers = [...this.allUsers]

      if (--this.batchesRemaining <= 0) {
         this.usersLoading = false

         if (this.pendingUserSelect) {
            const pending = this.pendingUserSelect
            this.pendingUserSelect = null
            setTimeout(() => (this.pendingUserSelect = pending))
         }
      }
   }

   private mapUser(u: any): any {
      return {
         revieweeUserId: u.revieweeUserId,
         revieweeDisplayName: u.revieweeDisplayName,
         practiceName: u.practiceName,
         practiceSKey: u.practiceSKey,
         cosUserId: u.cosUserId,
         vetLeaderUserId: u.vetLeaderUserId,
         rmUserId: u.rmUserId,
         hmUserId: u.hmUserId,
         directorUserId: u.directorUserId,
         vpUserId: u.vpUserId,
         cosDisplayName: u.cosDisplayName,
         vetLeaderDisplayName: u.vetLeaderDisplayName,
         rmDisplayName: u.rmDisplayName,
         hmDisplayName: u.hmDisplayName,
         hmDisplayName2: u.hmDisplayName2,
         directorDisplayName: u.directorDisplayName,
         vpDisplayName: u.vpDisplayName,
         jobTitleCode: u.jobTitleCode,
         isDeferred: u.isDeferred,
         deferReason: u.deferReason,
         deferralStatus: u.deferralStatus,
         cycle: u.cycle ? u.cycle : '',
         hireDate: new Date(u.hireDate),
         whenOpenCompletedBy: u.whenOpenCompletedBy,
         cosOverrideName: u.cosOverrideName,
         hmOverrideName: u.hmOverrideName,
         cosaOverrideName: u.cosaOverrideName,
         rmOverrideName: u.rmOverrideName,
         reviewType: u.reviewType,
         revieweeUserStatus: u.revieweeUserStatus,
         hasReview: u.hasReview,
         hasStarted: u.hasStarted,
         isCompleted: u.isCompleted,
         dvmSubmittedOn: u.selfSubmittedOn,
         cosSubmittedOn: u.reviewerSubmittedOn,
      }
   }

   /** Called by app-user-management via (usersChanged) when a reviewer or cycle is saved */
   onUsersChanged(updated: ReviewPlan[]): void {
      this.allUsers = updated
   }

   setReviewType(type: string): void {
      this.reviewType = type
      this.loadAnalytics()
   }

   setDvmCycle(cycle: 'Q1' | 'Q2' | 'Q3'): void {
      this.selectedDvmCycle = cycle
      this.reviewType = 'DVM'
      this.loadAnalytics()
   }

   private getReviewCycleParam(): string | undefined {
      return this.reviewType === 'DVM' && this.selectedDvmCycle === 'Q3' ? 'Q3' : undefined
   }

   refresh(): void {
      this.loadAnalytics()
   }

   formatPct(val: number): string {
      return Math.round(val * 100).toString()
   }

   loadAnalytics(): void {
      this.loading = true
      this.allAnalyticsLoaded = false
      this.error = null
      this.userReviewClient.getUserReviewsAll(this.reviewType, this.getReviewCycleParam()).subscribe({
         next: (reviews) => {
            this.allReviews = reviews
            this.userReviewClient.getReportingResponses(this.reviewType).subscribe({
               next: (allResponses) => {
                  this.allResponses = allResponses
                  const hospitals = new Set<string>()
                  const jobCodes = new Set<string>()
                  this.allReviews.forEach((review) => {
                     if (review.practiceName) hospitals.add(review.practiceName)
                     if (review.jobTitleCode) jobCodes.add(review.jobTitleCode)
                     if (review.cycle && review.jobTitleCode === 'VET') jobCodes.add(review.cycle)
                     else if (!review.cycle && review.jobTitleCode === 'VET') jobCodes.add('Q3')
                  })
                  this.hospitalOptions = Array.from(hospitals).sort()
                  this.jobCodeOptions = Array.from(jobCodes).sort()
                  this.applyFiltersAndBuildViewModel()
                  this.loading = false
                  this.allAnalyticsLoaded = true
               },
               error: () => {
                  this.error = 'Failed to load response data. Please try again.'
                  this.loading = false
                  this.allAnalyticsLoaded = true
               },
            })
         },
         error: () => {
            this.error = 'Failed to load data. Please try again.'
            this.loading = false
            this.allAnalyticsLoaded = true
         },
      })
   }

   private getReviewerNameFromReview(review: any): string | null {
      switch (this.reviewType) {
         case 'HM':
            return review.rmName
         case 'COS':
            return review.vetLeaderName
         case 'RM':
            return review.directorName
         case 'DO':
            return review.vpName
         default:
            return review.cosName
      }
   }

   private applyFiltersAndBuildViewModel(): void {
      let filtered = [...this.allReviews]
      if (this.filterHospital) filtered = filtered.filter((r) => r.practiceName === this.filterHospital)
      if (this.filterReviewer) filtered = filtered.filter((r) => this.getReviewerNameFromReview(r) === this.filterReviewer)
      if (this.filterJobCodes) filtered = filtered.filter((r) => r.jobTitleCode === this.filterJobCodes)
      if (this.filterCycle) filtered = filtered.filter((r) => r.cycle === this.filterCycle)
      if (this.filterSearch) {
         const s = this.filterSearch.toLowerCase()
         filtered = filtered.filter((r) => r.revieweeName?.toLowerCase().includes(s))
      }
      const filteredIds = new Set(filtered.map((r) => r.id))
      const filteredResponses = this.allResponses.filter((r) => filteredIds.has(r.performanceReviewId))
      this.buildViewModelWithData(filtered, filteredResponses)
   }

   private buildViewModelWithData(filteredReviews: any[], allResponses: any[]): void {
      const template = this.templates[this.reviewType]
      if (!template) return

      const totalReviews = filteredReviews.length
      const dvmSubmitted = filteredReviews.filter((r) => r.isDVMSubmitted === true || (r.dvmSubmittedOn && r.dvmSubmittedOn !== 'null')).length
      const cosSubmitted = filteredReviews.filter((r) => r.isCOSSubmitted === true || (r.cosSubmittedOn && r.cosSubmittedOn !== 'null')).length
      //const revCompleted = filteredReviews.filter((r) => r.isCOSSubmitted === true && r.isDVMSubmitted === true).length
      const revCompleted = filteredReviews.filter((r) => r.isCompleted).length

      const questionCards: QuestionCard[] = []
      let totalAgreement = 0
      let agreementCount = 0

      template.sections.forEach((section) => {
         section.questions.forEach((question) => {
            if (this.reviewType === 'COS' || this.reviewType === 'DO') {
               if (question.type !== 'radio' && question.type !== 'checkbox') return
            } else {
               if (question.type !== 'radio') return
            }

            const revieweeResponses = allResponses.filter(
               (r) => r.questionId === question.id && r.sectionId === section.id && r.respondentType === 'reviewee'
            )
            const reviewerResponses = allResponses.filter(
               (r) => r.questionId === question.id && r.sectionId === section.id && r.respondentType === 'reviewer'
            )
            if (!revieweeResponses.length && !reviewerResponses.length) return

            const revieweeDistribution = this.calculateDistribution(revieweeResponses, question.revieweeOptions || question.options)
            const reviewerDistribution = this.calculateDistribution(reviewerResponses, question.reviewerOptions || question.options)
            const agreementScore = this.calculateAgreementScore(revieweeResponses, reviewerResponses)

            if (agreementScore > 0) {
               totalAgreement += agreementScore
               agreementCount++
            }

            questionCards.push({
               leftTitle: `${section.revieweeTitle || section.reviewerTitle}`,
               subtitle: `› ${question.revieweeLabel || question.reviewerLabel}`,
               agreementScore,
               revieweeDistribution: revieweeDistribution.map((d) => ({ label: d.label, percent: d.percentage })),
               reviewerDistribution: reviewerDistribution.map((d) => ({ label: d.label, percent: d.percentage })),
            })
         })
      })

      const avgAgreement = agreementCount > 0 ? totalAgreement / agreementCount : 0
      const lendAHandYes = filteredReviews.filter((r) => r.lendAHandInterest === true).length
      const leaderYes = filteredReviews.filter((r) => r.leaderAsprations === true).length
      const topMedicalInterests = this.calculateMedicalInterests(allResponses)

      const recentlyUpdated = filteredReviews
         .filter((r) => r.dateUpdated)
         .sort((a, b) => new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime())
         .slice(0, 10)
         .map((r) => ({
            revieweeName: r.revieweeName || 'Unknown',
            hospital: r.practiceName || 'Unknown',
            status: this.getReviewStatus(r),
            updatedOn: new Date(r.dateUpdated),
         }))

      this.vm = {
         totalReviews,
         dvmSubmitted,
         cosSubmitted,
         avgAgreement,
         questionCards,
         lendAHandYes,
         leaderYes,
         topMedicalInterests,
         recentlyUpdated,
         revCompleted,
      }
   }

   get groupedQuestionCards(): { title: string; cards: QuestionCard[]; avgAgreement: number }[] {
      if (!this.vm) return []
      const map = new Map<string, QuestionCard[]>()
      for (const card of this.vm.questionCards) {
         if (card.leftTitle === 'Chief of Staff Job Description') continue
         if (!map.has(card.leftTitle)) map.set(card.leftTitle, [])
         map.get(card.leftTitle)!.push(card)
      }
      return Array.from(map.entries()).map(([title, cards]) => ({
         title,
         cards,
         avgAgreement: Math.round((cards.reduce((sum, c) => sum + c.agreementScore, 0) / cards.length) * 100),
      }))
   }

   private calculateMedicalInterests(allResponses: any[]): { label: string; count: number }[] {
      const ids = [
         'areas_to_develop',
         'growth_interests',
         'medical_interests',
         'areas_of_interest',
         'specialty_interests',
         'clinical_development_areas',
      ]
      const counts = new Map<string, number>()
      allResponses.forEach((r) => {
         if (ids.includes(r.questionId) && r.responseValue?.startsWith('[')) {
            try {
               JSON.parse(r.responseValue).forEach((v: string) => counts.set(v, (counts.get(v) || 0) + 1))
            } catch {}
         }
      })
      return Array.from(counts.entries())
         .map(([value, count]) => ({
            label: value
               .split('_')
               .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
               .join(' '),
            count,
         }))
         .sort((a, b) => b.count - a.count)
         .slice(0, 8)
   }

   private getReviewStatus(review: any): string {
      const isDvm = review.isDVMSubmitted || (review.dvmSubmittedOn && review.dvmSubmittedOn !== 'null')
      const isCos = review.isCOSSubmitted || (review.cosSubmittedOn && review.cosSubmittedOn !== 'null')
      if (review.isCompleted) return 'Complete'
      if (isDvm && isCos) return this.getReviewerLabel(true) + ' Submitted'
      if (isDvm) return this.getRevieweeLabel(true) + ' Submitted'
      return 'In Progress'
   }

   private calculateDistribution(responses: any[], options: any[]): { label: string; value: string; percentage: number; count: number }[] {
      if (!responses?.length || !options) return []
      const counts = new Map<string, number>()
      responses.forEach((r) => {
         if (!r.responseValue) return
         if (r.responseValue.startsWith('[')) {
            try {
               JSON.parse(r.responseValue).forEach((v: string) => counts.set(v, (counts.get(v) || 0) + 1))
            } catch {}
         } else {
            counts.set(r.responseValue, (counts.get(r.responseValue) || 0) + 1)
         }
      })
      return options
         .map((o) => {
            const count = counts.get(o.value) || 0
            return { label: o.label, value: o.value, count, percentage: responses.length > 0 ? Math.round((count / responses.length) * 100) : 0 }
         })
         .filter((d) => d.count > 0)
   }

   private calculateAgreementScore(revieweeResponses: any[], reviewerResponses: any[]): number {
      if (!revieweeResponses.length || !reviewerResponses.length) return 0
      const getValues = (responses: any[]): Set<string> => {
         const s = new Set<string>()
         responses.forEach((r) => {
            if (!r.responseValue) return
            if (r.responseValue.startsWith('[')) {
               try {
                  JSON.parse(r.responseValue).forEach((v: string) => s.add(v))
               } catch {}
            } else {
               s.add(r.responseValue)
            }
         })
         return s
      }
      const allValues = new Set([...getValues(revieweeResponses), ...getValues(reviewerResponses)])
      let totalDiff = 0
      allValues.forEach((value) => {
         const countIn = (responses: any[]) =>
            responses.filter((r) => {
               if (!r.responseValue) return false
               if (r.responseValue.startsWith('[')) {
                  try {
                     return JSON.parse(r.responseValue).includes(value)
                  } catch {
                     return false
                  }
               }
               return r.responseValue === value
            }).length
         const ep = revieweeResponses.length > 0 ? countIn(revieweeResponses) / revieweeResponses.length : 0
         const rp = reviewerResponses.length > 0 ? countIn(reviewerResponses) / reviewerResponses.length : 0
         totalDiff += Math.abs(ep - rp)
      })
      const agreement = Math.max(0, 1 - (allValues.size > 0 ? totalDiff / allValues.size : 0))
      return Math.round(agreement * 100) / 100
   }

   getRevieweeLabel(short = false, rt?: string): string {
      const type = rt || this.reviewType
      const map: Record<string, [string, string]> = {
         HM: ['HM', 'Hospital Manager'],
         COS: ['COS', 'Chief of Staff'],
         RM: ['RM', 'Regional Manager'],
         DO: ['DO', 'Director of Operations'],
      }
      return map[type] ? map[type][short ? 0 : 1] : short ? 'aDVM' : 'Associate DVM'
   }

   getReviewerLabel(short = false, rt?: string): string {
      const type = rt || this.reviewType
      const map: Record<string, [string, string]> = {
         HM: ['RM', 'Regional Manager'],
         COS: ['COSA', 'Chief of Staff Advisor'],
         RM: ['DO', 'Director of Operations'],
         DO: ['VP', 'Vice President of Operations'],
      }
      return map[type] ? map[type][short ? 0 : 1] : short ? 'COS' : 'Chief of Staff'
   }

   toggleFilters(): void {
      this.filtersCollapsed = !this.filtersCollapsed
   }

   clearFilters(): void {
      this.filterSearch = ''
      this.filterHospital = null
      this.filterReviewer = null
      this.filterJobCodes = null
      this.applyFiltersAndBuildViewModel()
   }

   onSearchChange(event: any): void {
      this.filterSearch = event.target.value
   }
   onSearchEnter(): void {
      this.applyFiltersAndBuildViewModel()
   }

   exportExcel(type: string): void {
      this.exportLoading = true
      this.clickedExportType = type
      const rt = this.exportReviewType || this.reviewType
      switch (type) {
         case 'reviewers':
         case 'role':
            this.exportReviewerAssignmentsXlsx(rt)
            break
         case 'individual':
         case 'group':
            this.exportQaXlsx(rt)
            break
         case 'aggregate':
         case 'hospital':
            this.exportAggregateXlsx(rt)
            break
         case 'audit':
            this.exportAuditXlsx()
            this.exportLoading = false
            break
         default:
            this.exportLoading = false
      }
   }

   getExportDesc(type: 'individual' | 'group' | 'reviewers' | 'aggregate'): string {
      const rtLabel = this.exportReviewType
         ? { DVM: 'aDVM', HM: 'HM', COS: 'COS', RM: 'RM', DO: 'DO' }[this.exportReviewType] || this.exportReviewType
         : null
      const revLabel = this.exportSelectedRevieweeId
         ? this.exportRevieweeOptions.find((u) => u.id === Number(this.exportSelectedRevieweeId))?.name || 'selected reviewee'
         : null
      const rt = rtLabel || 'all review types'
      const rev = revLabel || 'all reviewees'
      switch (type) {
         case 'individual':
            if (!rtLabel && !revLabel) return 'Every question + answer, self-assessment and review, for all reviewees'
            if (rtLabel && !revLabel) return `Every question + answer for all ${rt} reviewees - self-assessment and review`
            return `All questions + answers for ${rev} - self-assessment and reviewer responses`
         case 'group':
            return rtLabel
               ? `All questions + answers grouped by user - ${rt} reviews`
               : 'All questions + answers grouped by user across every review type'
         case 'reviewers':
            return rtLabel
               ? `Every ${rt} reviewee mapped to their COS / COSA / RM / DO / VP`
               : 'Every reviewee mapped to their COS / COSA / RM / DO / VP across all review types'
         case 'aggregate':
            return rtLabel
               ? `${rt} reviews - one row per question with answer breakdowns and agreement scores`
               : 'One row per question - answer option breakdown (count + %), self-assessment vs review side by side, with agreement score'
      }
   }

   onExportReviewTypeChange(): void {
      this.exportSelectedRevieweeId = null
      this.exportRevieweeOptions = []
      if (!this.exportReviewType) return
      this.exportRevieweeOptionsLoading = true
      this.userReviewClient.getUserReviewsAll(this.exportReviewType, undefined).subscribe({
         next: (reviews: any[]) => {
            const seen = new Set<number>()
            this.exportRevieweeOptions = reviews
               .map((r) => ({
                  id: Number(r.revieweeId ?? r.revieweeUserId ?? r.revieweeID ?? 0),
                  name: r.revieweeName || r.revieweeDisplayName || '',
               }))
               .filter((r) => r.id > 0 && r.name && !seen.has(r.id) && seen.add(r.id))
               .sort((a, b) => a.name.localeCompare(b.name))
            this.exportRevieweeOptionsLoading = false
         },
         error: () => {
            this.exportRevieweeOptionsLoading = false
         },
      })
   }

   private exportQaXlsx(reviewType: string): void {
      this.userReviewClient.getUserReviewsAll(reviewType, undefined).subscribe({
         next: (reviews: any[]) => {
            const reviewMap = new Map<number, any>()
            reviews.forEach((r) => {
               if (r.id != null) reviewMap.set(Number(r.id), r)
            })
            this.userReviewClient.getReportingResponses(reviewType).subscribe({
               next: (responses: any[]) => {
                  const filtered = this.exportSelectedRevieweeId
                     ? responses.filter((r) => {
                          const rev = reviewMap.get(r.performanceReviewId)
                          if (!rev) return false
                          return Number(rev.revieweeId ?? rev.revieweeUserId ?? rev.revieweeID) === Number(this.exportSelectedRevieweeId)
                       })
                     : responses

                  const template = this.templates[reviewType]
                  const secLabels = new Map<string, { ee: string; er: string }>()
                  const qMetas = new Map<string, { eeLabel: string; erLabel: string; eeOpts: any[]; erOpts: any[] }>()
                  template?.sections.forEach((sec) => {
                     secLabels.set(sec.id, { ee: sec.revieweeTitle || '', er: sec.reviewerTitle || '' })
                     sec.questions.forEach((q) =>
                        qMetas.set(`${sec.id}::${q.id}`, {
                           eeLabel: q.revieweeLabel || q.reviewerLabel || q.id,
                           erLabel: q.reviewerLabel || q.revieweeLabel || q.id,
                           eeOpts: q.revieweeOptions || q.options || [],
                           erOpts: q.reviewerOptions || q.options || [],
                        })
                     )
                  })

                  const decode = (val: string | null, opts: any[]): string => {
                     if (!val) return ''
                     if (val.startsWith('[')) {
                        try {
                           return JSON.parse(val)
                              .map((v: string) => opts.find((o) => o.value === v)?.label || v)
                              .join('; ')
                        } catch {
                           return val
                        }
                     }
                     return opts.find((o) => o.value === val)?.label || val
                  }

                  const byReview = new Map<number, any[]>()
                  filtered.forEach((r) => {
                     if (!byReview.has(r.performanceReviewId)) byReview.set(r.performanceReviewId, [])
                     byReview.get(r.performanceReviewId)!.push(r)
                  })
                  const sortedIds = [...byReview.keys()].sort((a, b) =>
                     (reviewMap.get(a)?.revieweeName || '').localeCompare(reviewMap.get(b)?.revieweeName || '')
                  )

                  const aoa: any[][] = [
                     [
                        this.xlsxHdr('Reviewee Name'),
                        this.xlsxHdr('Hospital'),
                        this.xlsxHdr('Review Type'),
                        this.xlsxHdr('Respondent'),
                        this.xlsxHdr('Section'),
                        this.xlsxHdr('Question'),
                        this.xlsxHdr('Answer'),
                     ],
                  ]

                  sortedIds.forEach((reviewId, idx) => {
                     const rev = reviewMap.get(reviewId)
                     const name = rev?.revieweeName || rev?.revieweeDisplayName || ''
                     const hospital = rev?.practiceName || ''
                     const rows = byReview.get(reviewId) || []
                     const addRows = (list: any[], isEe: boolean) => {
                        const label = isEe ? 'Self-assessment' : 'Review'
                        const bg = isEe ? 'E8F4FD' : 'E8F6F2'
                        const pill = isEe ? '0374B6' : '1BB5AE'
                        list.forEach((r) => {
                           const sec = secLabels.get(r.sectionId)
                           const qm = qMetas.get(`${r.sectionId}::${r.questionId}`)
                           const secL = this.stripHtml(isEe ? sec?.ee || r.sectionId : sec?.er || r.sectionId)
                           const qL = this.stripHtml(isEe ? qm?.eeLabel || r.questionId : qm?.erLabel || r.questionId)
                           const ans = decode(r.responseValue, isEe ? qm?.eeOpts || [] : qm?.erOpts || [])
                           aoa.push([
                              this.xlsxCell(name, bg),
                              this.xlsxCell(hospital, bg),
                              this.xlsxCell(reviewType, bg),
                              {
                                 v: label,
                                 s: {
                                    font: { sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
                                    fill: { fgColor: { rgb: pill }, patternType: 'solid' },
                                    alignment: { vertical: 'center' },
                                    border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
                                 },
                              },
                              this.xlsxCell(secL, bg),
                              this.xlsxCell(qL, bg),
                              this.xlsxCell(ans, bg),
                           ])
                        })
                     }
                     addRows(
                        rows.filter((r) => r.respondentType === 'reviewee'),
                        true
                     )
                     addRows(
                        rows.filter((r) => r.respondentType === 'reviewer'),
                        false
                     )
                     if (idx < sortedIds.length - 1)
                        aoa.push(Array(7).fill({ v: '', s: { fill: { fgColor: { rgb: 'F2F8F8' }, patternType: 'solid' } } }))
                  })

                  const revName = this.exportSelectedRevieweeId
                     ? this.exportRevieweeOptions.find((u) => u.id === Number(this.exportSelectedRevieweeId))?.name.replace(/\s+/g, '_') ||
                       'individual'
                     : 'all'
                  this.xlsxDownload(aoa, [22, 28, 12, 16, 28, 56, 70], 'Q&A Export', `qa_${reviewType || 'all'}_${revName}.xlsx`)
                  this.exportLoading = false
               },
               error: () => {
                  this.exportLoading = false
               },
            })
         },
         error: () => {
            this.exportLoading = false
         },
      })
   }

   private exportAggregateXlsx(reviewType: string): void {
      this.userReviewClient.getReportingResponses(reviewType).subscribe({
         next: (allResponses: any[]) => {
            const template = this.templates[reviewType]
            if (!template) {
               this.exportLoading = false
               return
            }
            const eeLabel = this.getRevieweeLabel(true, reviewType)
            const erLabel = this.getReviewerLabel(true, reviewType)
            const EE_BG = 'E8F4FD',
               ER_BG = 'E8F6F2',
               SEC_BG = 'D6EAE8'
            const aoa: any[][] = [
               [
                  this.xlsxHdr('Section'),
                  this.xlsxHdr('Question'),
                  this.xlsxHdr(`${eeLabel} Answer`),
                  this.xlsxHdr(`${eeLabel} Count`),
                  this.xlsxHdr(`${eeLabel} %`),
                  this.xlsxHdr(`${erLabel} Answer`),
                  this.xlsxHdr(`${erLabel} Count`),
                  this.xlsxHdr(`${erLabel} %`),
                  this.xlsxHdr('Agreement'),
               ],
            ]
            template.sections.forEach((section) => {
               section.questions.forEach((question) => {
                  if (reviewType === 'COS' || reviewType === 'DO') {
                     if (question.type !== 'radio' && question.type !== 'checkbox') return
                  } else {
                     if (question.type !== 'radio') return
                  }
                  const eeRes = allResponses.filter(
                     (r) => r.questionId === question.id && r.sectionId === section.id && r.respondentType === 'reviewee'
                  )
                  const erRes = allResponses.filter(
                     (r) => r.questionId === question.id && r.sectionId === section.id && r.respondentType === 'reviewer'
                  )
                  if (!eeRes.length && !erRes.length) return
                  const eeDist = this.calculateDistribution(eeRes, question.revieweeOptions || question.options || [])
                  const erDist = this.calculateDistribution(erRes, question.reviewerOptions || question.options || [])
                  const agree = this.calculateAgreementScore(eeRes, erRes)
                  const agreePct = agree > 0 ? `${Math.round(agree * 100)}%` : '—'
                  const secL = this.stripHtml(section.revieweeTitle || section.reviewerTitle || '')
                  const qL = this.stripHtml(question.revieweeLabel || question.reviewerLabel || '')
                  const maxLen = Math.max(eeDist.length, erDist.length, 1)
                  for (let i = 0; i < maxLen; i++) {
                     const ee = eeDist[i],
                        er = erDist[i],
                        first = i === 0
                     aoa.push([
                        this.xlsxCell(first ? secL : '', first ? SEC_BG : EE_BG, first),
                        this.xlsxCell(first ? qL : '', first ? SEC_BG : EE_BG, first),
                        this.xlsxCell(ee?.label || '', EE_BG),
                        this.xlsxCell(ee?.count ?? '', EE_BG),
                        this.xlsxCell(ee ? `${ee.percentage}%` : '', EE_BG),
                        this.xlsxCell(er?.label || '', ER_BG),
                        this.xlsxCell(er?.count ?? '', ER_BG),
                        this.xlsxCell(er ? `${er.percentage}%` : '', ER_BG),
                        this.xlsxCell(first ? agreePct : '', first ? SEC_BG : ER_BG, first, first ? '0F6E56' : '1A2B2A'),
                     ])
                  }
                  aoa.push(Array(9).fill({ v: '', s: { fill: { fgColor: { rgb: 'F2F8F8' }, patternType: 'solid' } } }))
               })
            })
            this.xlsxDownload(aoa, [30, 48, 28, 12, 10, 28, 12, 10, 14], 'Aggregate', `aggregate_${reviewType}.xlsx`)
            this.exportLoading = false
         },
         error: () => {
            this.exportLoading = false
         },
      })
   }

   private exportReviewerAssignmentsXlsx(reviewType: string): void {
      this.userReviewClient.getAllAppUsersWithRoles().subscribe({
         next: (users: any[]) => {
            const jobMap: Record<string, string[]> = { DVM: ['VET'], HM: ['HM', 'AREA_MANAGER'], COS: ['VET-COS'], RM: ['OPS-RM'], DO: ['CORP-DIR'] }
            const sorted = (reviewType ? users.filter((u) => jobMap[reviewType]?.includes(u.jobTitleCode)) : users).sort((a, b) =>
               (a.revieweeDisplayName || '').localeCompare(b.revieweeDisplayName || '')
            )
            const aoa: any[][] = [
               [
                  this.xlsxHdr('Reviewee Name'),
                  this.xlsxHdr('Hospital'),
                  this.xlsxHdr('Job Code'),
                  this.xlsxHdr('Cycle'),
                  this.xlsxHdr('COS'),
                  this.xlsxHdr('COSA'),
                  this.xlsxHdr('RM'),
                  this.xlsxHdr('HM'),
                  this.xlsxHdr('Director'),
                  this.xlsxHdr('VP'),
               ],
            ]
            sorted.forEach((u, i) => {
               const bg = i % 2 === 0 ? 'FFFFFF' : 'F5FAFA'
               aoa.push([
                  this.xlsxCell(u.revieweeDisplayName || '', bg),
                  this.xlsxCell(u.practiceName || '', bg),
                  this.xlsxCell(u.jobTitleCode || '', bg),
                  this.xlsxCell(u.cycle || '', bg),
                  this.xlsxCell(u.cosDisplayName || '', bg),
                  this.xlsxCell(u.vetLeaderDisplayName || '', bg),
                  this.xlsxCell(u.rmDisplayName || '', bg),
                  this.xlsxCell(u.hmDisplayName || '', bg),
                  this.xlsxCell(u.directorDisplayName || '', bg),
                  this.xlsxCell(u.vpDisplayName || '', bg),
               ])
            })
            this.xlsxDownload(aoa, [24, 30, 14, 22, 22, 22, 22, 22, 22], 'Reviewer Assignments', `reviewer_assignments_${reviewType || 'all'}.xlsx`)
            this.exportLoading = false
         },
         error: () => {
            this.exportLoading = false
         },
      })
   }

   private exportAuditXlsx(): void {
      const ACTION_COLORS: Record<string, string> = {
         'Reviewee Deferred': '03b6ad',
         'Reviewee Re-enabled': '03b6ad',
         'Primary Reviewer changed': '831183',
         'Reviewer changed': '0374b6',
         'Hospital Reviewer changed': '0374b6',
      }
      const aoa: any[][] = [
         [this.xlsxHdr('Timestamp'), this.xlsxHdr('Admin'), this.xlsxHdr('Action'), this.xlsxHdr('Target User'), this.xlsxHdr('Details')],
      ]
      this.auditLog.forEach((e, i) => {
         const bg = i % 2 === 0 ? 'FFFFFF' : 'F5FAFA'
         const actionRgb = ACTION_COLORS[e.action] || '444441'
         aoa.push([
            this.xlsxCell(e.timestamp ? new Date(e.timestamp).toLocaleString() : '', bg),
            this.xlsxCell(e.adminName || '', bg),
            {
               v: e.action || '',
               s: {
                  font: { sz: 10, bold: true, color: { rgb: actionRgb } },
                  fill: { fgColor: { rgb: bg }, patternType: 'solid' },
                  alignment: { vertical: 'center' },
                  border: { bottom: { style: 'hair', color: { rgb: 'EEEEEE' } } },
               },
            },
            this.xlsxCell(e.targetUserName || '', bg),
            this.xlsxCell(e.details || '', bg),
         ])
      })
      this.xlsxDownload(aoa, [22, 22, 18, 24, 50], 'Audit Log', 'audit_log.xlsx')
   }

   //  Audit log
   loadAuditLog(): void {
      this.auditLoading = true
      this.userReviewClient.getAuditLog(400, undefined, undefined).subscribe({
         next: (data) => {
            this.auditLog = data
            this.auditLoading = false
            this.allAuditLog = [...data]
         },
         error: () => {
            this.auditLoading = false
         },
      })
   }

   setAuditSort(column: 'timestamp' | 'adminName' | 'action' | 'rcReasonInput' | 'targetUserName'): void {
      if (this.sortAuditColumn === column) {
         this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
         this.sortAuditColumn = column
         this.sortDirection = 'asc'
      }
      this.auditLog = this.auditLog.sort((a, b) => {
         if (this.sortAuditColumn === 'timestamp') {
            const aDate = a.timestamp ? new Date(a.timestamp).getTime() : 0
            const bDate = b.timestamp ? new Date(b.timestamp).getTime() : 0
            return this.sortDirection === 'asc' ? aDate - bDate : bDate - aDate
         }
         let aVal = '',
            bVal = ''
         switch (this.sortAuditColumn) {
            case 'adminName':
               aVal = a.adminName || ''
               bVal = b.adminName || ''
               break
            case 'action':
               aVal = a.action || ''
               bVal = b.action || ''
               break
            case 'rcReasonInput':
               aVal = a.action || ''
               bVal = b.action || ''
               break
            case 'targetUserName':
               aVal = a.targetUserName || ''
               bVal = b.targetUserName || ''
               break
         }
         const cmp = aVal.localeCompare(bVal)
         return this.sortDirection === 'asc' ? cmp : -cmp
      })
   }

   onAuditFilter(filterType: number) {
      const selectedFilter = this.allFilters[filterType]
      this.auditTabs = filterType

      if (!selectedFilter) {
         this.auditLog = [...this.allAuditLog]
         return
      }

      if (selectedFilter === 'Deferred - Open Cycle') {
         this.auditLog = this.allAuditLog.filter((r) => r.action === 'Cycle Changed' && r.details?.includes('deferred and moved to the OPEN cycle'))
         return
      }

      if (selectedFilter === 'Cycle Changed') {
         const cycleFilters = ['Cycle Changed', 'Hospital Cycle Changed']

         this.auditLog = this.allAuditLog.filter(
            (r) => cycleFilters.includes(r.action) && !r.details?.includes('deferred and moved to the OPEN cycle')
         )
         return
      }

      if (selectedFilter === 'Reviewer Changed') {
         const reviewerFilters = ['Reviewer Changed', 'Primary Reviewer Changed', 'Hospital Reviewer Changed']

         this.auditLog = this.allAuditLog.filter((r) => reviewerFilters.includes(r.action))
         return
      }

      this.auditLog = this.allAuditLog.filter((r) => r.action === selectedFilter)
   }

   onScroll(): void {
      this.showBackToTop = window.pageYOffset > 300
   }
   scrollToTop(): void {
      window.scrollTo({ top: 0, behavior: 'smooth' })
   }

   //  XLSX helpers
   private stripHtml(html: string): string {
      return (
         html
            ?.replace(/<[^>]*>/g, '')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim() || ''
      )
   }
   private xlsxDownload(aoa: any[][], cols: number[], sheetName: string, filename: string): void {
      import('xlsx-js-style').then((XLSXStyle) => {
         const ws = XLSXStyle.utils.aoa_to_sheet(aoa)
         ws['!cols'] = cols.map((wch) => ({ wch }))
         ws['!freeze'] = { xSplit: 0, ySplit: 1 }
         const wb = XLSXStyle.utils.book_new()
         XLSXStyle.utils.book_append_sheet(wb, ws, sheetName)
         const buffer = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' })
         const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
         const a = document.createElement('a')
         a.href = url
         a.download = filename
         a.click()
         URL.revokeObjectURL(url)
      })
   }

   private xlsxHdr(v: string): any {
      return {
         v,
         s: {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
            fill: { fgColor: { rgb: '005E5D' }, patternType: 'solid' },
            alignment: { vertical: 'center', horizontal: 'left' },
            border: { bottom: { style: 'medium', color: { rgb: '1BB5AE' } } },
         },
      }
   }

   private xlsxCell(v: any, bg: string, bold = false, fontRgb = '1A2B2A'): any {
      return {
         v: v ?? '',
         s: {
            font: { sz: 10, bold, color: { rgb: fontRgb } },
            fill: { fgColor: { rgb: bg }, patternType: 'solid' },
            alignment: { vertical: 'center', wrapText: true },
            border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
         },
      }
   }

   onNavigateToUser(row: any): void {
      this.activeTab = 'users'
      this.pendingUserSelect = row
   }

   formatDate(date: string): string {
      return dayjs.utc(date).local().format('ddd, MMM DD, h:mm A')
   }
}
